import { Component, PuzzleButton } from "puzzle";
import React from "react";
import { GridColumnMenuProps } from "@progress/kendo-react-grid";
import * as _ from "lodash";
import ColumnMenu from "puzzle/components/grid/columnmenu/ColumnMenu";
import {
  commonLocalizationGetter,
  getStatusesLocalizationPath,
  quickFilterChangeDelay,
  parseRemoteTagFieldData
} from "../util/Util";

type REMOTE_TAG_FIELD_DATA = {
  index: number
  size: number
  success: boolean
  supportedValues: { name: string, value: string }[]
  totalCount: number
  version: string
};

// All filter types should be added here
export const FilterTypes = {
  dropDown: "COMBO_MULTI",
  text: "INPUT",
  date: "DATE_RANGE",
  number: "NUMBER",
  singleCombo: "COMBO_SINGLE",
  remoteTagField: "REMOTE_TAG_FIELD"
};

function gridFilter(GridComponent: any) {
  return class extends Component<any, any> {
    gridRef: any;

    constructor(props: any) {
      super(props);
      this.gridRef = React.createRef();
    };

    /**
     * Creates config object for quick filter
     * @param filter
     */
    createFilterConfigObject = (filter: any) => {
      let comboData, filterObject;
      filterObject = {
        filterName: (this.gridRef.GET_DATAINDEX_MAP as any)[filter.name],
        filterType: filter.type,
      };
      if (filter.type === 'COMBO_MULTI' || filter.type === 'COMBO_SINGLE') {
        comboData = filter.values.map((option: any) => {
          return {
            name: option[0],
            value: option[1]
          }
        });
        filterObject = {
          ...filterObject,
          filterValues: comboData
        }
      } else if (filter.type === 'REMOTE_TAG_FIELD') {
        filterObject = {
          ...filterObject,
          valuesProvider: filter.valuesProvider
        }
      }
      return filterObject
    };


    /**
     * Returns current filter values for given dataIndex
     */
    getFilterData(dataIndex: string) {
      const { quickFiltersData } = this.gridRef.state;
      let result = [], fieldDataIndexSplit;
      if (quickFiltersData?.filters) {
        const { filters } = quickFiltersData;
        for (let i = 0; i < filters.length; i++) {
          fieldDataIndexSplit = filters[i].field.split('.')[0];
          if (fieldDataIndexSplit === dataIndex) {
            result = filters[i].value;
            break;
          }
        }
      }
      return result
    }

    /**
     * Returns config data for filed with current dataIndex
     * @param dataIndex
     */
    getFilterValues(dataIndex: string) {
      let result = [];
      const { filterConfigData } = this.gridRef.state;
      if (filterConfigData) {
        for (let i = 0; i < filterConfigData.length; i++) {
          if (filterConfigData[i].filterName === dataIndex) {
            result = filterConfigData[i].filterValues;

            if (['dueDate', 'phaseDueDate'].includes(dataIndex)) {
              result = result.map((item: { name: string, value: string }) => {
                return {
                  value: item.value,
                  name: this.gridRef.translate(`${dataIndex}.${item.name}`)
                }
              });
            }
            break;
          }
        }
      }
      return result
    }

    /**
     * If field is filterable returns filed else returns false;
     */
    isColumnFilterable(dataIndex: string) {
      let result = false;
      const { filterConfigData } = this.gridRef.state;
      const filterData = filterConfigData;
      if (filterData) {
        for (let i = 0; i < filterData.length; i++) {
          if (filterData[i].filterName === dataIndex) {
            result = filterData[i];
            break;
          }
        }
      }
      return result
    }

    /**
     * Filtering main grid with params from quick filter
     */
    prefilterMainGrid = () => {
      const {
        quickFiltersData
      } = this.gridRef.state;
      const { favoritesOnly } = this.props;
      let filterData: any = {};
      let currentFilter: any;
      let filterName: string;
      let dataIndex: string;
      if (quickFiltersData.length) {
        const filters = quickFiltersData;
        for (let i = 0; i < filters.length; i++) {
          currentFilter = filters[i];
          dataIndex = currentFilter.field.split('.')[0];
          filterName = (this.gridRef.GET_FILTERNAME_MAP as any)[dataIndex] || 'customControlValues';
          if (_.isArray(currentFilter.value) || !_.isEmpty(currentFilter.value) || _.isNumber(currentFilter.value)) {
            if (filterName === 'customControlValues') {
              const filterItem = {
                name: dataIndex,
                value: currentFilter.value
              };
              filterData[filterName] = filterData[filterName] ? [...filterData[filterName]].concat(filterItem) : [filterItem]
            } else {
              filterData[filterName] = currentFilter.value;
            }
          }
        }
      }
      if (this.gridRef.checkboxSelectionEnabled && this.gridRef.checkboxSelectionClear) {
        this.gridRef.checkboxSelectionClear();
      }
      this.gridRef.dispatch(this.gridRef.props.actionsNames.filter, '', {
        mainGridFilter: filterData,
        clearSearchBar: true,
        favoritesOnly
      });
    };

    /**
     * Returns an array of config objects for quick filters
     * @param filterData
     */
    prepareQuickFilterConfig = (filterData: any) => {
      const keys = Object.keys(filterData);
      const data: any = [];
      let filter, filterObject;
      for (let key of keys) {
        filter = filterData[key];
        filterObject = this.createFilterConfigObject(filter);
        if (!_.isEmpty(filterObject)) {
          data.push(filterObject);
        }
      }
      return data;
    };

    /**
     * Filter header render
     */
    renderFilterHeader = () => {
      let clearFilterBtn: null | React.ReactElement<PuzzleButton> = null;
      if (this.props.quickFiltersEnabled) {
        let disabled = !(this.gridRef?.state?.quickFiltersData?.length || this.gridRef?.state?.activeSearchbar)
        clearFilterBtn = <PuzzleButton
          aria-label={this.gridRef?.translate?.call(this.gridRef, 'quickFilters')}
          themeColor={'primary'}
          fillMode="outline"
          disabled={disabled}
          withTooltip={true}
          titleText={this.gridRef?.translate?.call(this.gridRef, 'quickFilters')}
          className="tp-with-bg pd-clear-filter-btn"
          iconClass='tp-icon tp-clear-filters'
          onClick={this.clearQuickFilter}
        >
        </PuzzleButton>
      }

      return clearFilterBtn;
    };

    /**
     * Clear quick filter's data
     * @param withRefresh
     */
    clearQuickFilter = (withRefresh: any = true) => {
      if (this.gridRef.checkboxSelectionEnabled && this.gridRef.checkboxSelectionClear) {
        this.gridRef.checkboxSelectionClear();
      }
      this.gridRef.setState({
        activeSearchbar: false,
        quickFiltersData: []
      }, () => {
        if (withRefresh) {
          this.prefilterMainGrid();
        }
      });
    };

    /**
     * Method for rendering filter column menu
     * @param props :GridColumnMenuProps
     * */
    renderColumnMenu = (props: GridColumnMenuProps): JSX.Element => {
      const columnName = props.column.field?.split('.')[0] || '';
      const values = this.gridRef.state.quickFiltersData.find((item: any) => item.field === columnName)?.value || [];
      let data = this.getFilterValues(columnName);
      let columnMenuProps: any = {
        data,
        closeMenu: props.onCloseMenu,
        filterable: this.gridRef.props.quickFiltersEnabled && this.isColumnFilterable(columnName),
        onSortChange: this.gridRef.onSortChange.bind(this.gridRef),
        searchType: 'fullText',
        sortable: this.gridRef.sortableColumns.includes(props.column.field || ''),
        values: _.isArray(values) ? values : [values]
      };

      if (['quoteStatus', 'status', 'taskStatus', 'priority', 'budgetStatus'].includes(columnName)) {
        const path = getStatusesLocalizationPath(columnName);
        let translationProp: 'value' | 'name' = 'value';
        if (columnName === 'priority') {
          translationProp = 'name';
        }

        data = data.map((item: { name: string, value: string }) => {
          return {
            value: item.value,
            name: commonLocalizationGetter(`${path}.${item[translationProp]}`)
          }
        });

        if (columnName === 'quoteStatus') {
          data = _.sortBy(data, 'name')
        }

        if (columnName === 'priority') {
          data = _.orderBy(data, ['value'], ['desc']);
        }

        columnMenuProps.data = data;
      }
      let column: any = this.isColumnFilterable(columnName);
      if (column) {
        switch (column.filterType) {
          case FilterTypes.number:
            columnMenuProps.type = 'input-numeric';
            break;
          case FilterTypes.text:
            columnMenuProps.type = 'input';
            break;
          case FilterTypes.dropDown :
            columnMenuProps.type = 'list-search';
            if (['quoteStatus', 'status', 'taskStatus', 'priority', 'budgetStatus'].includes(columnName)) {
              columnMenuProps.type = 'list';
            }
            break;
          case FilterTypes.singleCombo:
            columnMenuProps.type = 'list';
            columnMenuProps.singleSelectList = true;
            break;
          case FilterTypes.remoteTagField:
            columnMenuProps = {
              ...columnMenuProps,
              parseData: (data: REMOTE_TAG_FIELD_DATA) => parseRemoteTagFieldData(data, columnName),
              requestConfig: {
                url: '/remoteCombo',
                method: 'post',
                params: { valuesProvider: column.valuesProvider }
              },
              type: 'virtual-dom-list-search'
            }
            break;
          default:
            columnMenuProps.type = 'input';
            break;
        }
      }
      // @ts-ignore
      return <ColumnMenu
        setActiveHeader={this.gridRef.setOpenFilter.bind(this, columnMenuProps)}
        column={{ field: columnName }}
        onFilterChange={this.onFilterChange}
        {...columnMenuProps}
      />
    };

    /**
     * Handles grids filter change
     *
     * @param field
     * @param value
     */
    onFilterChange = (field: string, value: string[]): void => {
      const {
        filterConfigData,
        quickFiltersData
      } = this.gridRef.state;
      let quickFilters;

      if (value.length) {
        quickFilters = [...quickFiltersData];
        let existingFilter = quickFilters.find((item: any) => field === item.field);
        let filterType = filterConfigData.find((item: any) => field === item.filterName).filterType;

        if (![FilterTypes.dropDown, FilterTypes.remoteTagField].includes(filterType)) {
          (value as unknown as string) = value[0];
        }

        if (existingFilter) {
          if (!!value) {
            existingFilter.value = value;
          } else {
            quickFilters = quickFiltersData.filter((item: any) => field !== item.field)
          }
        } else {
          quickFilters.push({
            field,
            value
          })
        }
      } else {
        quickFilters = quickFiltersData.filter((item: any) => field !== item.field)
      }

      this.gridRef.setState({ quickFiltersData: quickFilters }, this.debouncedPrefilterMainGrid);
    };

    /**
     * Used for delaying refresh of page that triggers on filter change
     */
    debouncedPrefilterMainGrid = _.debounce(() => {
      this.prefilterMainGrid();
    }, quickFilterChangeDelay);

    /**
     * Reference callback
     */
    refCallback = (ref: any) => {
      if (ref) {
        this.gridRef = ref;

        if (!this.gridRef.state.filterConfigData) {
          throw new Error('filterConfigData need to be defined in Grid Component state');
        }

        if (!this.gridRef.state.quickFiltersData) {
          throw new Error('quickFiltersData need to be defined in Grid Component state');
        }

        if (this.gridRef.state.quickFiltersVisible === undefined) {
          throw new Error('quickFiltersVisible need to be defined in Grid Component state');
        }

        if (!this.gridRef.props.actionsNames.filter) {
          throw new Error('actionsNames.filter need to be defined in Grid Component props');
        }
      }
    };

    /**
     * Expose resources to GridComponent
     */
    GRID_FILTER = {
      onFilterChange: this.onFilterChange,
      prepareQuickFilterConfig: this.prepareQuickFilterConfig,
      renderFilterHeader: this.renderFilterHeader,
      prefilterMainGrid: this.prefilterMainGrid,
      renderColumnMenu: this.renderColumnMenu
    };

    render() {
      return (
        <GridComponent {...this.props} ref={this.refCallback} gridFilter={this.GRID_FILTER} />
      )
    }
  }
}

export default gridFilter;
